import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { ApiKeyStatus, ModelPrefs } from "@/types/settings";

export const settingsKeys = {
  apiKey: ["settings", "api-key"] as const,
  geminiKey: ["settings", "gemini-key"] as const,
  claudeAccount: ["settings", "claude-account"] as const,
  entitlements: ["settings", "entitlements"] as const,
  models: ["settings", "models"] as const,
  styleProfile: ["settings", "style-profile"] as const,
};

/** Tenant brand style profile — injected into every generation for the
 *  team. All fields optional; server drops empties. */
export interface StyleProfile {
  art_style?: string;
  motion?: string;
  palette?: string;
  typography?: string;
  notes?: string;
}

// ── Local agent: concurrent generation slots ──────────────────────────────

export function useAgentConcurrencyQuery() {
  return useQuery({
    queryKey: ["settings", "agent-concurrency"] as const,
    queryFn: () => apiFetch<{ gen_concurrency: number }>("/settings/agent-concurrency"),
    staleTime: 30 * 1000,
  });
}

export function useSetAgentConcurrencyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (genConcurrency: number) =>
      apiFetch<{ gen_concurrency: number }>("/settings/agent-concurrency", {
        method: "PUT",
        body: JSON.stringify({ gen_concurrency: genConcurrency }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["settings", "agent-concurrency"], data);
    },
  });
}

// ── OpenAI credentials (engine lands in Phase B; storage/UI now) ──────────

export function useOpenAiKeyStatusQuery() {
  return useQuery({
    queryKey: ["settings", "openai-key"] as const,
    queryFn: () => apiFetch<{ configured: boolean }>("/settings/openai-key"),
    staleTime: 30 * 1000,
  });
}

export function useSetOpenAiKeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) =>
      apiFetch<{ saved: boolean }>("/settings/openai-key", {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "openai-key"] });
    },
  });
}

export function useDeleteOpenAiKeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: boolean }>("/settings/openai-key", { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "openai-key"] });
    },
  });
}

export function useOpenAiAccountStatusQuery() {
  return useQuery({
    queryKey: ["settings", "openai-account"] as const,
    queryFn: () => apiFetch<{ configured: boolean }>("/settings/openai-account"),
    staleTime: 30 * 1000,
  });
}

export function useSetOpenAiAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (authJson: string) =>
      apiFetch<{ saved: boolean }>("/settings/openai-account", {
        method: "PUT",
        body: JSON.stringify({ api_key: authJson }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "openai-account"] });
    },
  });
}

export function useDeleteOpenAiAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: boolean }>("/settings/openai-account", { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "openai-account"] });
    },
  });
}

/** One model the current user's auth actually grants. */
export interface AvailableModel {
  id: string;
  label: string;
  /** Generation backend: "claude" | "openai" | "gemini". */
  provider: string;
  source:
    | "api_key"
    | "platform_key"
    | "claude_account"
    | "server_cli"
    | "gemini_key";
}

export interface AvailableModels {
  /** Providers this user can generate with right now. */
  providers: string[];
  /** Server default generation provider. */
  default_provider?: string;
  /** Server default Claude model (Auto's final fallback). */
  claude_default?: string;
  /** OpenAI account's default model (what Codex runs without -m). */
  openai_default?: string;
  /** Server-configured OpenCode default (OPENCODE_MODEL). */
  opencode_default?: string;
  text: AvailableModel[];
  image: AvailableModel[];
}

export function useAvailableModelsQuery() {
  return useQuery({
    queryKey: ["settings", "available-models"] as const,
    queryFn: () => apiFetch<AvailableModels>("/settings/available-models"),
    // Live provider queries behind it — cache for a minute, refetch quietly.
    staleTime: 60 * 1000,
  });
}

export function useOpencodeKeyStatusQuery() {
  return useQuery({
    queryKey: ["settings", "opencode-key"] as const,
    queryFn: () => apiFetch<{ configured: boolean }>("/settings/opencode-key"),
    staleTime: 30 * 1000,
  });
}

export function useSetOpencodeKeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) =>
      apiFetch<{ saved: boolean }>("/settings/opencode-key", {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "opencode-key"] });
    },
  });
}

export function useDeleteOpencodeKeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: boolean }>("/settings/opencode-key", { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "opencode-key"] });
    },
  });
}

export function useAgentStatusQuery() {
  return useQuery({
    queryKey: ["renders", "agent-status"] as const,
    queryFn: () => apiFetch<{ alive: boolean }>("/renders/agent/status"),
    refetchInterval: 10 * 1000,
  });
}

/** Generation-agent status — same local agent process (it heartbeats both
 *  endpoints), but this one also reports which provider CLIs the agent
 *  detected on that machine, so the card can show what generations will
 *  route locally. */
export function useGenAgentStatusQuery() {
  return useQuery({
    queryKey: ["generations", "agent-status"] as const,
    queryFn: () =>
      apiFetch<{ alive: boolean; providers: string[] }>(
        "/generations/agent/status",
      ),
    refetchInterval: 10 * 1000,
  });
}

export function useCreateAgentTokenMutation() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ token: string }>("/settings/agent-token", { method: "POST" }),
  });
}

export function useStyleProfileQuery() {
  return useQuery({
    queryKey: settingsKeys.styleProfile,
    queryFn: () => apiFetch<StyleProfile>("/settings/style-profile"),
    staleTime: 30 * 1000,
  });
}

export function useSetStyleProfileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profile: StyleProfile) =>
      apiFetch<StyleProfile>("/settings/style-profile", {
        method: "PUT",
        body: JSON.stringify(profile),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.styleProfile, data);
    },
  });
}

export function useApiKeyStatusQuery() {
  return useQuery({
    queryKey: settingsKeys.apiKey,
    queryFn: () => apiFetch<ApiKeyStatus>("/settings/api-key"),
    staleTime: 30 * 1000,
  });
}

export function useSetApiKeyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKey: string) =>
      apiFetch<ApiKeyStatus>("/settings/api-key", {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.apiKey, data);
    },
  });
}

export function useDeleteApiKeyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<ApiKeyStatus>("/settings/api-key", {
        method: "DELETE",
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.apiKey, data);
    },
  });
}

export function useGeminiKeyStatusQuery() {
  return useQuery({
    queryKey: settingsKeys.geminiKey,
    queryFn: () => apiFetch<{ configured: boolean }>("/settings/gemini-key"),
    staleTime: 30 * 1000,
  });
}

export function useSetGeminiKeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) =>
      apiFetch<{ saved: boolean }>("/settings/gemini-key", {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.geminiKey });
    },
  });
}

export function useDeleteGeminiKeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: boolean }>("/settings/gemini-key", {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.geminiKey });
    },
  });
}

export function useModelPrefsQuery() {
  return useQuery({
    queryKey: settingsKeys.models,
    queryFn: () => apiFetch<ModelPrefs>("/settings/models"),
    staleTime: 30 * 1000,
  });
}

export function useSetModelPrefsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prefs: ModelPrefs) =>
      apiFetch<ModelPrefs>("/settings/models", {
        method: "PUT",
        body: JSON.stringify(prefs),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.models, data);
      queryClient.invalidateQueries({ queryKey: settingsKeys.models });
    },
  });
}

export function useClaudeAccountStatusQuery() {
  return useQuery({
    queryKey: settingsKeys.claudeAccount,
    queryFn: () => apiFetch<{ configured: boolean }>("/settings/claude-account"),
    staleTime: 30 * 1000,
  });
}

export function useSetClaudeAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      apiFetch<{ saved: boolean }>("/settings/claude-account", {
        method: "PUT",
        body: JSON.stringify({ api_key: token }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.claudeAccount });
    },
  });
}

export function useDeleteClaudeAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: boolean }>("/settings/claude-account", {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.claudeAccount });
    },
  });
}

export function useEntitlementsQuery() {
  return useQuery({
    queryKey: settingsKeys.entitlements,
    queryFn: () => apiFetch<{ byok: boolean }>("/settings/entitlements"),
    staleTime: 60 * 1000,
  });
}
